import type { StatusRole } from '@lighthouse/design'
import type { EtlJob, EtlJobCatalog } from '../../types/etlJobs'
import type { EtlRunDetail, EtlRunOutcome, RunExecutionHealth } from '../../types/etlRuns'
import type { RequestedRun, ResumeRule, StaleRule, WindowCoverage } from '../../types/etlProgress'
import { NOT_MEASURED, agoLabel, duration, exactNumber } from '../../utils/format'
import { entityOutcomeLook } from '../outcomeFormat'

/**
 * The ETL Explorer's pure wording and decisions: outcome labels, the Rerun /
 * Resume button, coverage and window text, and the Scheduler link.
 *
 * Kept out of the page so each rule can be pinned by a test without rendering
 * anything — every sentence here is a claim about how the worker behaves, and a
 * claim that drifts from the worker is worse than no claim.
 *
 * Imports only types, utils/format (which imports only services/apiError) and
 * the pure components/outcomeFormat, so it loads under vitest without MSAL.
 */

// ---------------------------------------------------------------------------
// Outcomes
// ---------------------------------------------------------------------------

/**
 * Every outcome the Explorer can render — the entity grain of the served
 * vocabulary (ExecutionOutcomes.ForEntity), `skipped` included: the runs
 * endpoint serves a skipped entity as `skipped`, as Execution History does.
 * Kept as an alias so the Explorer's call sites need not change.
 */
export type ExplorerOutcome = EtlRunOutcome

/**
 * How an outcome is drawn. `neutral` is a plain gray Badge: StatusRole has no
 * neutral member (design-system types.ts:14), and a run nobody has started is
 * neither good nor a problem.
 */
export type OutcomeTone = { kind: 'status'; role: StatusRole } | { kind: 'neutral' }

export interface OutcomeDisplay {
  label: string
  tone: OutcomeTone
}

/**
 * The label and tone for an outcome — components/outcomeFormat.ts's, the one
 * map Execution History, the Overview and the Scheduler also read, because they
 * link to one another and a row must read the same on each. A value this build
 * does not know renders as itself on a neutral badge — never as a plausible
 * known outcome, and never green. `halted` and `cancelled` are run outcomes an
 * entity row never has, so here they are unknown too.
 */
export function outcomeDisplay(outcome: ExplorerOutcome | (string & {})): OutcomeDisplay {
  const look = entityOutcomeLook(outcome)
  return { label: look.label, tone: look.role ? { kind: 'status', role: look.role } : { kind: 'neutral' } }
}

/** Why a stale row is stale, by the worker's own rule — the badge's tooltip and the banner. */
export function staleExplanation(rule: StaleRule): string {
  return `Recorded as running, but no heartbeat for more than ${rule.minutes} min — the worker's own rule`
    + `${rule.published ? '' : ' (code default)'}. Nothing marks a dead worker's run as failed, so it may never finish.`
}

/** The tooltip an outcome badge carries, or null when the label says it all. */
export function outcomeHint(outcome: ExplorerOutcome | (string & {}), rule: StaleRule | null): string | null {
  if (outcome === 'stale') {
    return rule
      ? staleExplanation(rule)
      : 'Recorded as running, but its heartbeat is older than the worker\'s stale rule. '
        + 'Nothing marks a dead worker\'s run as failed, so it may never finish.'
  }
  if (outcome === 'skipped') {
    return 'The source table or view does not exist, so nothing was read. Recorded as skipped rather than '
      + 'failed, so a resume does not keep re-checking it.'
  }
  if (outcome === 'pending') return 'Enrolled in its run but not started.'
  if (outcome === 'cancelled') {
    return 'Stopped because its run was cancelled. Not a failure: what it merged stays merged, its unmerged landed rows '
      + 'are discarded, and its watermark did not move.'
  }
  return null
}

// ---------------------------------------------------------------------------
// Durations, windows, coverage
// ---------------------------------------------------------------------------

/**
 * The duration cell. A stale row's end is unknown, so it says when the worker
 * was last heard from instead of a number that would read as "took this long".
 */
export function durationText(run: {
  outcome: ExplorerOutcome | (string & {})
  totalDurationSeconds: number | null
  lastHeartbeatAt: string | null
}): string {
  if (run.outcome === 'stale') {
    return run.lastHeartbeatAt ? `last heard ${agoLabel(run.lastHeartbeatAt)}` : 'no heartbeat recorded'
  }
  return run.totalDurationSeconds == null ? NOT_MEASURED : duration(run.totalDurationSeconds)
}

/**
 * A 0..1 share as a percentage, floored to one decimal.
 *
 * Floored, not rounded: 99.96% of a window must not print "100%", which would
 * say the window is finished; and a sliver above zero prints "<0.1" rather than
 * "0", which would say nothing was done.
 */
export function percentOfWindow(share: number): string {
  if (share >= 1) return '100'
  if (share <= 0) return '0'
  const floored = Math.floor(share * 1000) / 10
  if (floored === 0) return '<0.1'
  return Number.isInteger(floored) ? String(floored) : floored.toFixed(1)
}

/**
 * The window-coverage readout: a share of the recorded date WINDOW, never of
 * rows — or the server's reason it withheld one, verbatim.
 */
export function coverageText(coverage: WindowCoverage): string {
  if (coverage.share == null) {
    return coverage.unavailableReason ?? 'Not measured, and the server gave no reason.'
  }
  return `${percentOfWindow(coverage.share)}% of the recorded date window transformed — a share of the window, not of rows`
}

/** What the run was created to load: an entity, a module, or the whole warehouse. */
export function requestedTargetText(requested: RequestedRun | null): string {
  if (!requested) return NOT_MEASURED
  if (requested.entity) return requested.entity
  if (requested.module) return `Module ${requested.module}`
  return 'Whole warehouse'
}

/** The recorded window: plain dates, the end exclusive, as WarehouseEltJob recorded them. */
export function requestedWindowText(requested: RequestedRun | null): string {
  if (!requested) return NOT_MEASURED
  return `${requested.start} to ${requested.end} (end exclusive)`
}

/**
 * The watermark's label. Only a completed row holds a value its own completion
 * left; anywhere else it was copied from the entity's last completed load.
 *
 * "As this run left it" rather than "advanced by this run": a completed row
 * reports the watermark even when the run did not move it (the value it
 * inherited is kept when nothing newer was read), so "advanced" would claim
 * progress the row does not record.
 */
export function watermarkLabel(fromThisRun: boolean): string {
  return fromThisRun
    ? 'Watermark (as this run\'s completion left it)'
    : 'Watermark (from the last completed load of this entity)'
}

/** The outcomes of a row that has finished — the only rows whose Warnings and Errors flags are written. */
const FINISHED_OUTCOMES: ReadonlySet<string> = new Set(['succeeded', 'succeededWithWarnings', 'failed', 'skipped', 'cancelled'])

/** Said instead of a Warnings or Errors flag the row has not been given yet. */
export const RECORDED_AT_COMPLETION = 'Recorded when the entity finishes'

/**
 * A Warnings or Errors cell. Both are 0/1 FLAGS, not counts, and both are written once, when the entity
 * finishes (EtlRunTelemetry.CompleteEntityAsync) — so a finished row reads Yes or No, and a row still
 * running, stale or not started reads the dash with why: its "0" is a column default, not a reading.
 * An outcome this build does not know is not assumed finished.
 */
export function completionFlagText(
  flag: number,
  outcome: ExplorerOutcome | (string & {}),
): { value: string; note: string | null } {
  if (!FINISHED_OUTCOMES.has(outcome)) return { value: NOT_MEASURED, note: RECORDED_AT_COMPLETION }
  return { value: flag > 0 ? 'Yes' : 'No', note: null }
}

/** "3 of 5", or — for a row with no chunk rows at all — that none are recorded, never "0 of 0". */
export function chunksTransformedText(run: Pick<EtlRunDetail, 'chunksTransformed' | 'chunkCount'>): string {
  return run.chunkCount === 0
    ? 'No chunks recorded'
    : `${exactNumber(run.chunksTransformed)} of ${exactNumber(run.chunkCount)}`
}

/** Success rate, or the dash: null over nothing extracted, never "0%" or "100%". */
export function successRateText(health: Pick<RunExecutionHealth, 'successRatePercent'>): string {
  return health.successRatePercent == null ? NOT_MEASURED : `${health.successRatePercent}%`
}

/** What the live strip can say about a run the stage table has not caught up with yet. */
export function liveStageCaption(
  run: Pick<EtlRunDetail, 'livePhase' | 'liveChunkIndex'> & { outcome: ExplorerOutcome | (string & {}) },
): string | null {
  if (run.outcome !== 'running' && run.outcome !== 'stale') return null
  const chunk = run.liveChunkIndex
  const live = run.livePhase === 'landing' && chunk != null
    ? `Landing chunk ${chunk + 1}`
    : run.livePhase === 'transforming' && chunk != null
      ? `Transforming chunk ${chunk + 1}`
      : NOT_MEASURED
  return `Stages are recorded when the entity finishes. Live: ${live}`
}

// ---------------------------------------------------------------------------
// Rerun / Resume
// ---------------------------------------------------------------------------

/** Case-insensitive, as WarehouseEltJob and the dispatcher compare entity names. */
function sameName(a: string, b: string): boolean {
  return a.toLowerCase() === b.toLowerCase()
}

/**
 * The catalogue job that loads this entity: category `Entity` and a display
 * name equal to the entity name.
 *
 * DisplayName is what the dispatcher passes as the entity name
 * (EtlJobTriggerDispatcher.cs:137-145). The job id is lower-cased for Hangfire,
 * so `'elt-' + entity` is never re-derived here — the catalogue is the only list.
 */
export function findEntityJob(catalog: EtlJobCatalog | undefined, entityName: string): EtlJob | null {
  return catalog?.jobs.find((job) => job.category === 'Entity' && sameName(job.displayName, entityName)) ?? null
}

/**
 * The Scheduler deep link for a rerun: the job, plus the window this run was
 * recorded with.
 *
 * Both dates are dropped when the recorded window belongs to a request for
 * ANOTHER entity: resume matches on orchestrator and mode only, so an entity
 * run can sit inside a pass created for a different entity, whose window says
 * nothing about this one. The Scheduler then shows the job's own defaults.
 */
export function schedulerRerunLink(
  jobId: string,
  run: { entityName: string; requested: RequestedRun | null },
): string {
  const params = new URLSearchParams()
  params.set('job', jobId)
  const requested = run.requested
  if (requested && !recordedForAnotherEntity(run)) {
    if (requested.start) params.set('startDate', requested.start)
    if (requested.end) params.set('endDate', requested.end)
  }
  return `/jobs?${params.toString()}`
}

/** True when the pass was created by a request for a different entity than this row's. */
export function recordedForAnotherEntity(run: { entityName: string; requested: RequestedRun | null }): boolean {
  const recorded = run.requested?.entity
  return recorded != null && !sameName(recorded, run.entityName)
}

/** Which resume case the button is in. */
export type ResumeCase = 'this-run' | 'other-run' | 'new-run' | 'resume-off' | 'unknown'

export interface RerunPlan {
  resumeCase: ResumeCase
  label: 'Resume this run' | 'Rerun' | 'Rerun (new run)'
  /** Draw a warning icon: the request would be recorded inside a DIFFERENT run. */
  warn: boolean
  /** The matched catalogue job, or null when none matches. */
  job: EtlJob | null
  /** Null when the button is enabled. */
  disabledReason: string | null
  /** The tooltip: what a request would do, and the one-at-a-time rule. */
  note: string
  /** The Scheduler link, or null without a job. */
  href: string | null
}

export type CatalogState = 'loading' | 'error' | 'ready'

/** The concurrency sentence every Rerun tooltip ends with. WarehouseEltJob.cs:59 [SkipConcurrentExecution]. */
export const ONE_ENTITY_LOAD_AT_A_TIME =
  'Only one entity load runs at a time; a request made while another is running is dropped.'

/** The tail both "a run is live" sentences share. The lock is per job METHOD (SkipConcurrentExecutionAttribute.cs:44-47). */
const DROPPED_IF_ENTITY_LOAD = 'an entity load now is dropped if the running load is an entity load'

type RerunSubject = Pick<EtlRunDetail, 'executionId' | 'runKey' | 'entityName' | 'requested'> & {
  outcome: ExplorerOutcome | (string & {})
}

function resumeCaseOf(run: RerunSubject, resume: ResumeRule | null | undefined): ResumeCase {
  if (!resume) return 'unknown'
  if (!resume.enabled) return 'resume-off'
  if (!resume.targetRunId) return 'new-run'
  return resume.targetRunId === run.executionId ? 'this-run' : 'other-run'
}

function targetDescription(resume: ResumeRule): string {
  if (resume.targetEntity) return `a load of ${resume.targetEntity}`
  if (resume.targetModule) return `a warehouse load of module ${resume.targetModule}`
  return 'a warehouse load'
}

function resumeNote(run: RerunSubject, resume: ResumeRule | null | undefined, resumeCase: ResumeCase): string {
  if (!resume || resumeCase === 'unknown') {
    return 'The API did not say whether resume is on, so whether a request reopens an earlier run or starts '
      + 'a new one is not known.'
  }

  const defaults = resume.rulePublished
    ? ''
    : ' These resume settings are the code defaults; Automaton has not published its own.'

  switch (resumeCase) {
    case 'this-run':
      return `Resume is on: a request for ${run.entityName} inside the ${resume.windowHours}h window reopens this run `
        + `(${run.runKey}) and replays its unfinished chunks with the bounds they were recorded with, rather than `
        + `starting over${resume.targetIsLive ? `. That run is in progress, so ${DROPPED_IF_ENTITY_LOAD}` : ''}.`
        + defaults
    case 'other-run': {
      const key = resume.targetRunKey ?? resume.targetRunId
      const state = `${resume.targetStatus ?? 'status not recorded'}`
        + `${resume.targetIsStale ? ', no heartbeat — its worker appears lost' : ''}`
      return `Resume is on: a new request will reopen run ${key} (${state}, ${targetDescription(resume)}) — `
        + 'resume matches on orchestrator and mode only — so this load would be recorded inside that run'
        + `${resume.targetIsLive ? `; that run is in progress, so ${DROPPED_IF_ENTITY_LOAD}` : ''}.`
        + defaults
    }
    case 'new-run':
      return `Resume is on, but there is no failed or unfinished run in the last ${resume.windowHours}h to reopen, `
        + 'so a request starts a new run.' + defaults
    case 'resume-off':
      return 'Resume is off, so a request starts a new run.' + defaults
  }
}

function labelOf(resumeCase: ResumeCase): RerunPlan['label'] {
  switch (resumeCase) {
    case 'this-run': return 'Resume this run'
    case 'other-run': return 'Rerun'
    // Unknown is not "new run": that would be a claim nothing served.
    case 'unknown': return 'Rerun'
    case 'new-run':
    case 'resume-off':
      return 'Rerun (new run)'
  }
}

function disabledReasonOf(
  run: RerunSubject,
  catalog: EtlJobCatalog | undefined,
  catalogState: CatalogState,
  job: EtlJob | null,
): string | null {
  if (catalogState === 'loading') return 'Loading the job catalogue.'
  if (catalogState === 'error' || !catalog) {
    return 'The job catalogue could not be loaded, so which job loads this entity is not known.'
  }
  if (!job) {
    return `No Entity job for ${run.entityName} is in the job catalogue, so there is nothing to rerun it with.`
  }
  if (catalog.permission && !catalog.permission.canTriggerJobs) {
    return catalog.permission.deniedReason ?? 'The server says this session may not run jobs.'
  }
  const halt = catalog.activeHalt
  if (halt && !(halt.exemptCategories ?? []).includes('Entity')) {
    return `ETL is halted (${halt.reason}, run ${halt.runKey}). Entity loads are refused until the halt is cleared.`
  }
  if (run.outcome === 'running') {
    // CHECKER C11. The lock is per job method, so which request is dropped depends
    // on the load now holding this row — which nothing on the row records.
    return `In progress; a request made now would be dropped by the one-at-a-time lock if the running load is an `
      + 'entity load, and recorded inside this run if it is a warehouse load.'
  }
  return null
}

/**
 * Everything the Rerun / Resume button shows and does, from the served
 * catalogue alone.
 *
 * Resume is ON by default (EtlResilienceOptions.cs:75), so a "Rerun" inside the
 * resume window REOPENS a run rather than starting one — the button says which,
 * from the target the catalogue serves. No "from the failed stage" semantics are
 * invented: the resume path replays unfinished chunks, and that is all it says.
 */
export function rerunPlan(
  run: RerunSubject,
  catalog: EtlJobCatalog | undefined,
  catalogState: CatalogState,
): RerunPlan {
  const job = catalogState === 'ready' ? findEntityJob(catalog, run.entityName) : null
  const resumeCase = resumeCaseOf(run, catalog?.resume)
  const dates = job && run.requested && recordedForAnotherEntity(run)
    ? ` The window recorded on this run belongs to a ${run.requested.entity} request, so the Scheduler opens with `
      + 'the job\'s default window instead.'
    : ''

  return {
    resumeCase,
    label: labelOf(resumeCase),
    warn: resumeCase === 'other-run',
    job,
    disabledReason: disabledReasonOf(run, catalog, catalogState, job),
    note: `${resumeNote(run, catalog?.resume, resumeCase)} ${ONE_ENTITY_LOAD_AT_A_TIME}${dates}`
      + ' This opens the Scheduler with the job filled in; nothing is requested until you run it there.',
    href: job ? schedulerRerunLink(job.jobId, run) : null,
  }
}

/** The modules the list's filter offers: those of the catalogue's Entity jobs, as served. Never invented. */
export function entityModules(catalog: EtlJobCatalog | undefined): string[] {
  const modules = new Set<string>()
  for (const job of catalog?.jobs ?? []) {
    if (job.category === 'Entity' && job.module) modules.add(job.module)
  }
  return [...modules].sort((a, b) => a.localeCompare(b))
}
